import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function LifeSpanPanel({ profileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.lifespan(profileId)); }
    catch (e) {
      console.error("lifespan failed:", e);
      toast.error(e?.response?.data?.detail || "Failed to load life span");
    }
    setLoading(false);
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[#A19E95] font-mono text-sm">Assessing longevity… (~60s first time)</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="lifespan-panel">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 border border-[#2A2D35] bg-[#181A1F] p-5 flex flex-col">
          <div className="text-[10px] uppercase tracking-widest text-[#A8896C]">Modern-Age Estimate</div>
          <div className="font-heading text-5xl text-[#D4AF37] mt-3" data-testid="lifespan-band">
            {data.modern_estimate_band}
          </div>
          <p className="mt-3 text-sm text-[#A19E95] font-light">{data.band_reasoning}</p>
          <div className="text-[10px] text-[#6E6B64] font-mono mt-auto pt-4">
            Calibrated against modern medicine · Not a hard death date.
          </div>
        </div>
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#7B906F] mb-3">Protective Factors</div>
          <ul className="space-y-2 text-sm">
            {(data.protective_factors || []).map((p, i) => (
              <li key={`pf-${i}-${p.slice(0,20)}`} className="text-[#E6E1D6] pl-3 border-l-2 border-[#7B906F]/40">{p}</li>
            ))}
          </ul>
        </div>
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#C15C3D] mb-3">Risk Factors</div>
          <ul className="space-y-2 text-sm">
            {(data.risk_factors || []).map((p, i) => (
              <li key={`rf-${i}-${p.slice(0,20)}`} className="text-[#E6E1D6] pl-3 border-l-2 border-[#C15C3D]/40">{p}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Vulnerability Windows</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(data.vulnerability_windows || []).map((w, i) => (
            <div key={`vw-${i}-${w.age_range}`} className="border border-[#2A2D35] p-3 bg-[#0F1115]"
                 data-testid={`vuln-window-${i}`}>
              <div className="flex items-center justify-between">
                <div className="font-heading text-lg text-[#E5C158]">Ages {w.age_range}</div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#A8896C]">{w.system}</span>
              </div>
              <div className="text-xs text-[#A19E95] font-light mt-2">{w.reason}</div>
            </div>
          ))}
        </div>
      </div>

      {data.recommendations?.length > 0 && (
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#7B906F] mb-3">Recommendations</div>
          <ul className="space-y-2 text-sm">
            {data.recommendations.map((r, i) => (
              <li key={`rec-${i}-${r.slice(0,20)}`} className="text-[#E6E1D6] pl-3 border-l-2 border-[#7B906F]/40">{r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
